import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface RunnerGoals {
  primary: string | null;
  secondary: string[];
}

export interface RunnerProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  city: string | null;
  avatar_url: string | null;
  preferred_distances: string[];
  level: 'beginner' | 'intermediate' | 'advanced' | 'elite';
  goal_2025: string | null;
  goals_2026: RunnerGoals;
  birth_date: string | null;
  phone: string | null;
  nickname: string | null;
  clothing_size: string | null;
  pps_code: string | null;
  gender: string | null;
  // Medical document fields
  medical_doc_type: 'pps' | 'license' | null;
  medical_doc_code: string | null;
  medical_doc_url: string | null;
  medical_doc_expires_at: string | null;
  medical_doc_status: 'pending' | 'validated' | 'rejected' | null;
  medical_doc_validated_at: string | null;
  medical_doc_validated_by: string | null;
  medical_doc_rejection_reason: string | null;
  medical_doc_license_type: string | null;
  nationality: string | null;
  results_display_consent: boolean | null;
  results_consent_at: string | null;
  ffa_club_code: string | null;
  created_at: string;
  updated_at: string;
}

export function useRunnerProfile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get userId once to ensure stable hook order
  const userId = user?.id;

  const { data: profile, isLoading } = useQuery({
    queryKey: ['runner-profile', userId],
    queryFn: async () => {
      if (!userId) return null;
      
      const { data, error } = await supabase
        .from('runner_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      
      // Parse goals_2026 from JSON
      const goalsData = data.goals_2026 as { primary?: string | null; secondary?: string[] } | null;
      const goals_2026: RunnerGoals = {
        primary: goalsData?.primary || data.goal_2025 || null,
        secondary: Array.isArray(goalsData?.secondary) ? goalsData.secondary : []
      };
      
      return {
        ...data,
        goals_2026,
        medical_doc_type: data.medical_doc_type as 'pps' | 'license' | null,
        medical_doc_status: data.medical_doc_status as 'pending' | 'validated' | 'rejected' | null,
      } as RunnerProfile;
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
  });

  const createProfile = useMutation({
    mutationFn: async (data: Partial<RunnerProfile>) => {
      if (!userId) throw new Error('User not authenticated');

      const { data: profile, error } = await supabase
        .from('runner_profiles')
        .insert({
          id: userId,
          first_name: data.first_name,
          last_name: data.last_name,
          city: data.city,
          avatar_url: data.avatar_url,
          preferred_distances: data.preferred_distances || [],
          level: data.level || 'intermediate',
          goal_2025: data.goals_2026?.primary || data.goal_2025,
          goals_2026: data.goals_2026 || { primary: null, secondary: [] },
          birth_date: data.birth_date,
          phone: data.phone,
          nickname: data.nickname,
          clothing_size: data.clothing_size,
          pps_code: data.pps_code,
          gender: data.gender,
          nationality: data.nationality || 'FRA',
          results_display_consent: data.results_display_consent ?? false,
          ffa_club_code: data.ffa_club_code,
        } as any)
        .select()
        .single();

      if (error) throw error;
      
      const goalsData = (profile as any).goals_2026 as { primary?: string | null; secondary?: string[] } | null;
      return {
        ...profile,
        goals_2026: {
          primary: goalsData?.primary || null,
          secondary: Array.isArray(goalsData?.secondary) ? goalsData.secondary : []
        }
      } as RunnerProfile;
    },
    onSuccess: (profile) => {
      queryClient.setQueryData(['has-runner-profile', userId], true);
      queryClient.setQueryData(['runner-profile', userId], profile);
      queryClient.invalidateQueries({ queryKey: ['runner-profile'] });
      queryClient.invalidateQueries({ queryKey: ['has-runner-profile'] });
      toast({
        title: 'Profil créé',
        description: 'Bienvenue dans la communauté Kourse !',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateProfile = useMutation({
    mutationFn: async (data: Partial<RunnerProfile>) => {
      if (!userId) throw new Error('User not authenticated');

      const updateData: Record<string, any> = {};
      
      if (data.first_name !== undefined) updateData.first_name = data.first_name;
      if (data.last_name !== undefined) updateData.last_name = data.last_name;
      if (data.city !== undefined) updateData.city = data.city;
      if (data.avatar_url !== undefined) updateData.avatar_url = data.avatar_url;
      if (data.preferred_distances !== undefined) updateData.preferred_distances = data.preferred_distances;
      if (data.level !== undefined) updateData.level = data.level;
      if (data.goals_2026 !== undefined) {
        updateData.goal_2025 = data.goals_2026?.primary;
        updateData.goals_2026 = data.goals_2026;
      } else if (data.goal_2025 !== undefined) {
        updateData.goal_2025 = data.goal_2025;
      }
      if (data.birth_date !== undefined) updateData.birth_date = data.birth_date;
      if (data.phone !== undefined) updateData.phone = data.phone;
      if (data.nickname !== undefined) updateData.nickname = data.nickname;
      if (data.clothing_size !== undefined) updateData.clothing_size = data.clothing_size;
      if (data.pps_code !== undefined) updateData.pps_code = data.pps_code;
      if (data.gender !== undefined) updateData.gender = data.gender;
      if (data.medical_doc_type !== undefined) updateData.medical_doc_type = data.medical_doc_type;
      if (data.medical_doc_code !== undefined) updateData.medical_doc_code = data.medical_doc_code;
      if (data.medical_doc_url !== undefined) updateData.medical_doc_url = data.medical_doc_url;
      if (data.medical_doc_expires_at !== undefined) updateData.medical_doc_expires_at = data.medical_doc_expires_at;
      if (data.medical_doc_status !== undefined) updateData.medical_doc_status = data.medical_doc_status;
      if (data.medical_doc_license_type !== undefined) updateData.medical_doc_license_type = data.medical_doc_license_type;
      if (data.medical_doc_rejection_reason !== undefined) updateData.medical_doc_rejection_reason = data.medical_doc_rejection_reason;
      if (data.medical_doc_validated_at !== undefined) updateData.medical_doc_validated_at = data.medical_doc_validated_at;
      if (data.nationality !== undefined) updateData.nationality = data.nationality;
      if (data.results_display_consent !== undefined) updateData.results_display_consent = data.results_display_consent;
      if (data.ffa_club_code !== undefined) updateData.ffa_club_code = data.ffa_club_code;

      const { data: profile, error } = await supabase
        .from('runner_profiles')
        .upsert({ id: userId, ...updateData }, { onConflict: 'id' })
        .select()
        .single();

      if (error) throw error;
      return profile;
    },
    onSuccess: () => {
      // A new row may have just been inserted for a user that never had one
      // (e.g. buyers coming from the bib marketplace). Mark the profile as
      // existing so the runner/volunteer space nav updates immediately.
      queryClient.setQueryData(['has-runner-profile', userId], true);
      queryClient.invalidateQueries({ queryKey: ['runner-profile'] });
      queryClient.invalidateQueries({ queryKey: ['has-runner-profile'] });
      queryClient.invalidateQueries({ queryKey: ['runner-profile-completeness'] });
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Subscribe to realtime updates - placed after all other hooks for stable order
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`runner-profile-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'runner_profiles',
          filter: `id=eq.${userId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['runner-profile', userId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  return {
    profile,
    isLoading,
    createProfile,
    updateProfile,
  };
}
